import * as React from 'react';
import { Bullseye, Spinner } from '@patternfly/react-core';
import { LOGGED_IN_QUERY_PARAM } from '~/analytics/AnalyticsService';
import { monitoringService } from '~/monitoring';
import { AuthContextType } from './type';
import { useAuthAnalytics } from './useAuthAnalytics';
import { setUserDataToLocalStorage } from './utils';

const redirectToLogin = () => {
  const returnPath = `${window.location.pathname}?${LOGGED_IN_QUERY_PARAM}=1`;
  window.location.replace(`/oauth2/sign_in?rd=${encodeURIComponent(returnPath)}`);
};

export const AuthContext = React.createContext<AuthContextType>({
  user: { email: null, preferredUsername: null },
  isAuthenticated: false,
});

export const AuthProvider: React.FC<React.PropsWithChildren> = React.memo(({ children }) => {
  const [user, setUser] = React.useState<AuthContextType['user']>({
    email: null,
    preferredUsername: null,
  });
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const { onLogout } = useAuthAnalytics();

  React.useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const userData = await fetch('/oauth2/userinfo');
        if (userData?.status === 401) {
          redirectToLogin();
        } else {
          const data = (await userData.json()) as AuthContextType['user'];
          setUser(data);
          setUserDataToLocalStorage({
            email: data.email,
            preferredUsername: data.preferredUsername,
          });
          setIsAuthenticated(true);

          monitoringService?.setUser({
            id: data.preferredUsername ?? undefined,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error while Authenticating the user', err);
      }
    };
    void checkAuthStatus();
  }, []);

  const signOut = async () => {
    // Awaited so the logout/journey analytics events actually reach the
    // network before we navigate away (see useAuthAnalytics#onLogout).
    await onLogout();
    await fetch('/oauth2/sign_out');
    redirectToLogin();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        signOut,
      }}
    >
      {isAuthenticated ? (
        children
      ) : (
        <Bullseye>
          <Spinner />
        </Bullseye>
      )}
    </AuthContext.Provider>
  );
});
